<template>
  <form
    ref="searchForm"
    class="form-inline search-full col"
    action="#"
    method="get"
    @submit.prevent=""
    :class="{ open: searchOpen, 'has-results': hasResults }"
  >
    <div class="form-group w-100">
      <div class="Typeahead Typeahead--twitterUsers">
        <div class="u-posRelative">
          <input
            ref="searchInput"
            class="demo-input Typeahead-input form-control-plaintext w-100"
            type="text"
            v-model="terms"
            placeholder="Search ryd Partner Portal ..."
            name="q"
            title=""
            autofocus
            autocomplete="off"
            @keydown="handleInputKeydown"
          />
          <div class="spinner-border Typeahead-spinner" role="status" v-if="isLoading">
            <span class="sr-only">Loading...</span>
          </div>
          <vue-feather class="close-search" type="x" @click="search_close()" />
        </div>

        <div v-if="hasResults" class="Typeahead-menu-container">
          <div id="foundResultMenu" class="Typeahead-menu is-open">
            <div v-if="filteredMenuItems.length > 0">
              <h5>Search in Navigation Menu</h5>
              <div
                class="ProfileCard u-cf found-item"
                v-for="(menuItem, index) in filteredMenuItems"
                :key="`menu-${index}`"
                :class="{ 'is-highlighted': highlightedIndex === index }"
                :ref="(el) => setResultRef(index, el)"
                @click="setActiveLink(menuItem)"
                @mouseenter="highlightedIndex = index"
              >
                <div class="ProfileCard-avatar header-search">
                  <svg class="stroke-icon">
                    <use
                      :xlink:href="iconSprite + `#${menuItem.icon}`"
                    ></use>
                  </svg>
                </div>
                <div class="ProfileCard-details">
                  <div class="ProfileCard-realName">
                    <span class="realname font-primary">
                      <i class="icofont icofont-link font-primary">&nbsp;</i>
                      {{ menuItem.displayPath }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="visibleFaqMatches.length > 0">
              <h5>FAQ matches</h5>
              <div
                class="ProfileCard u-cf found-item"
                v-for="(faqMatch, index) in visibleFaqMatches"
                :key="`faq-${index}`"
                :class="{ 'is-highlighted': highlightedIndex === faqStartIndex + index }"
                :ref="(el) => setResultRef(faqStartIndex + index, el)"
                @click="setActiveLink(faqMatch)"
                @mouseenter="highlightedIndex = faqStartIndex + index"
              >
                <div class="ProfileCard-details">
                  <div class="ProfileCard-realName">
                    <span class="realname font-primary">
                      <i class="icofont icofont-question-circle font-primary">&nbsp;</i>
                      {{ faqMatch.faqQuestion }}
                    </span>
                  </div>
                  <div>
                    <p class="faq-answer">{{ faqMatch.faqAnswer }}</p>
                    <small class="text-muted">
                      <i class="icofont icofont-link">&nbsp;</i> {{ faqMatch.displayPath }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="visiblePageContents.length > 0">
              <h5>Search In Page Keywords</h5>
              <div
                class="ProfileCard u-cf found-item"
                v-for="(pageContent, index) in visiblePageContents"
                :key="`page-${index}`"
                :class="{ 'is-highlighted': highlightedIndex === pageStartIndex + index }"
                :ref="(el) => setResultRef(pageStartIndex + index, el)"
                @click="setActiveLink(pageContent)"
                @mouseenter="highlightedIndex = pageStartIndex + index"
              >
                <div class="ProfileCard-details">
                  <div class="ProfileCard-realName">
                    <span class="realname font-primary">
                      <i class="icofont icofont-link font-primary">&nbsp;</i>
                      {{ pageContent.displayPath }}
                    </span>
                  </div>
                  <div>
                    <p v-html="pageContent.contentSnippet"></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="searchResultEmpty && terms.trim()" class="Typeahead-menu is-open">
          <div class="tt-dataset tt-dataset-0">
            <div class="EmptyMessage">Oops, there are no results found.</div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import iconSprite from '@/assets/svg/icon-sprite.svg';
import { mapState } from 'vuex';
import { menuItems } from '@/constants/menu';
import { getSearchRegistryEntry } from '@/constants/search-registry';
import { extractTextContent } from '@/utils/textExtractor';
import { isSubFleetPartner } from '@/utils/subFleet';
import { getPortalDataEnvironment } from '@/utils/portalEnvironment';

const FAQ_RESULT_LIMIT = 8;
const PAGE_RESULT_LIMIT = 8;

export default {
  name: 'SearchBar',
  data() {
    return {
      terms: '',
      searchResult: false,
      searchResultEmpty: false,
      pageContents: [],
      results: [],
      userData: JSON.parse(localStorage.getItem('userAddData')) || {},
      isLoading: false,
      highlightedIndex: 0,
      resultRefs: {},
    };
  },
  computed: {
    ...mapState({
      menuItems: (state) => state.menu.data,
      searchOpen: (state) => state.menu.searchOpen,
      searchResults: (state) => state.menu.searchData,
    }),
    filteredMenuItems() {
      return this.results.filter((result) => result.type === 'menu');
    },
    filteredPageContents() {
      return this.results.filter((result) => result.type === 'page');
    },
    filteredFaqMatches() {
      return this.results.filter((result) => result.type === 'faq');
    },
    visibleFaqMatches() {
      return this.filteredFaqMatches.slice(0, FAQ_RESULT_LIMIT);
    },
    visiblePageContents() {
      return this.filteredPageContents.slice(0, PAGE_RESULT_LIMIT);
    },
    faqStartIndex() {
      return this.filteredMenuItems.length;
    },
    pageStartIndex() {
      return this.faqStartIndex + this.visibleFaqMatches.length;
    },
    visibleResults() {
      return [...this.filteredMenuItems, ...this.visibleFaqMatches, ...this.visiblePageContents];
    },
    hasResults() {
      return this.results.length > 0;
    },
  },
  watch: {
    terms: {
      handler(value) {
        if (this.searchOpen) {
          this.debouncedSearch(value);
        }
      },
    },
    results() {
      this.highlightedIndex = 0;
      this.resultRefs = {};
    },
    searchOpen: {
      handler(newVal) {
        if (newVal) {
          this.addFix();
          this.$nextTick(() => {
            this.$refs.searchInput.focus();
            document.addEventListener('mousedown', this.handleOutsideClick);
          });
        } else {
          this.removeFix();
          document.removeEventListener('mousedown', this.handleOutsideClick);
        }
      },
      immediate: true,
    },
    hasResults: {
      handler(newVal) {
        if (newVal) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      },
      immediate: true,
    },
    highlightedIndex() {
      this.scrollHighlightedIntoView();
    },
  },
  created() {
    this.indexPageContents();
    this.debouncedSearch = this.debounce(this.performSearch, 300);
  },
  mounted() {
    document.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeUnmount() {
    document.body.classList.remove('no-scroll');
    document.removeEventListener('mousedown', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    handleOutsideClick(event) {
      if (this.$refs.searchForm && !this.$refs.searchForm.contains(event.target)) {
        this.search_close();
      }
    },
    handleGlobalKeydown(event) {
      if (event.defaultPrevented) {
        return;
      }

      const isSearchShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';

      if (isSearchShortcut) {
        event.preventDefault();
        this.search_open();
        return;
      }

      if (event.key === 'Escape' && this.searchOpen) {
        event.preventDefault();
        this.search_close();
      }
    },
    handleInputKeydown(event) {
      const resultCount = this.visibleResults.length;
      if (!resultCount) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % resultCount;
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.highlightedIndex = (this.highlightedIndex - 1 + resultCount) % resultCount;
        return;
      }

      if (event.key === 'Enter') {
        const item = this.visibleResults[this.highlightedIndex];
        if (item) {
          event.preventDefault();
          this.setActiveLink(item);
        }
      }
    },
    setResultRef(index, el) {
      if (el) {
        this.resultRefs[index] = el;
      } else {
        delete this.resultRefs[index];
      }
    },
    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const el = this.resultRefs[this.highlightedIndex];
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    search_open() {
      this.$store.commit('menu/SET_SEARCH_OPEN', true);
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    search_close() {
      this.$store.commit('menu/SET_SEARCH_OPEN', false);
      this.removeFix();
      this.terms = '';
      this.results = [];
      this.searchResultEmpty = false;
      this.highlightedIndex = 0;
      this.resultRefs = {};
    },
    indexPageContents() {
      const traverseMenu = (items, parentTitles = [], parentRequirements = []) => {
        for (const item of items) {
          const currentRequirements = [
            ...parentRequirements,
            {
              roles: item.requiredRoles || [],
              groups: item.requiredUserGroup || [],
              partnerTypes: item.requiredPartnerTypes || [],
              excludedPartners: item.excludedPartners || [],
              hiddenForSubFleet: item.hiddenForSubFleet || false,
              requiresSubFleet: item.requiresSubFleet || false,
              hiddenInProduction: item.hiddenInProduction || false,
              hiddenForInternalUsers: item.hiddenForInternalUsers || false,
            },
          ];

          let component = item.component;
          if (!component && item.path) {
            const resolved = this.$router.resolve(item.path);
            const leafRecord = resolved.matched?.[resolved.matched.length - 1];
            component = leafRecord?.components?.default ?? leafRecord?.component;
          }

          const registryEntry = getSearchRegistryEntry(item.path);

          if (component || item.keywords || registryEntry) {
            let textContent = '';
            let keywords = '';

            if (component) {
              textContent = extractTextContent(component);
              if (component.keywords) {
                keywords = component.keywords.join(' | ');
              }
            }

            if (item.keywords) {
              const itemKeywords = Array.isArray(item.keywords)
                ? item.keywords.join(' | ')
                : item.keywords;
              keywords = keywords ? `${keywords} | ${itemKeywords}` : itemKeywords;
            }

            const registryParts = [];
            if (registryEntry?.keywords?.length) {
              registryParts.push(registryEntry.keywords.join(' | '));
            }
            if (registryEntry?.questions?.length) {
              registryParts.push(registryEntry.questions.join(' | '));
            }
            if (registryEntry?.faqs?.length) {
              registryParts.push(registryEntry.faqs.map((faq) => `${faq.q} ${faq.a}`).join(' | '));
            }
            const registryContent = registryParts.join(' | ');

            const combinedContent = [keywords, registryContent, textContent]
              .filter((part) => part && part.trim())
              .join(' ');

            if (combinedContent.trim()) {
              this.pageContents.push({
                id: item.path,
                title: item.title,
                content: combinedContent,
                faqs: registryEntry?.faqs ?? [],
                requiredRoles: item.requiredRoles || [],
                requiredUserGroup: item.requiredUserGroup || [],
                requiredPartnerTypes: item.requiredPartnerTypes || [],
                excludedPartners: item.excludedPartners || [],
                hiddenForSubFleet: item.hiddenForSubFleet || false,
                requiresSubFleet: item.requiresSubFleet || false,
                hiddenInProduction: item.hiddenInProduction || false,
                hiddenForInternalUsers: item.hiddenForInternalUsers || false,
                requirements: currentRequirements,
                displayPath: [...parentTitles, item.title].join(' -> '),
                path: item.path,
                type: 'page',
              });
            }
          }
          if (item.children) {
            traverseMenu(item.children, [...parentTitles, item.title], currentRequirements);
          }
        }
      };
      traverseMenu(menuItems.data);
    },
    performSearch(value) {
      this.isLoading = true;
      const userAddData = JSON.parse(localStorage.getItem('userAddData')) || {};
      const userRoles = userAddData.roles || [];
      const userGroup = userAddData.userGroup || '';
      const userPartnerTypes = userAddData.partnerTypes || [];
      const isSubFleet = isSubFleetPartner(userAddData.partnerSettings);
      const isInternalUser = userPartnerTypes.includes('RYD_INTERNAL') || userGroup === 'ryd';
      const searchval = value.toLowerCase();
      let items = [];

      const checkAccess = (requirements) => {
        // requirements is an object { roles, groups, partnerTypes, excludedPartners, hiddenForSubFleet, requiresSubFleet }
        const {
          roles,
          groups,
          partnerTypes,
          excludedPartners,
          hiddenForSubFleet,
          requiresSubFleet,
          hiddenInProduction,
          hiddenForInternalUsers,
        } = requirements;

        // Check Roles
        const hasRoles =
          !roles || roles.length === 0 || roles.some((role) => userRoles.includes(role));

        // Check User Group (Partner Name)
        const hasGroup = !groups || groups.length === 0 || groups.includes(userGroup);

        // Check Partner Types
        const hasPartnerType =
          !partnerTypes ||
          partnerTypes.length === 0 ||
          partnerTypes.some((type) => userPartnerTypes.includes(type));

        // Check Excluded Partners
        const isExcluded = excludedPartners && excludedPartners.includes(userGroup);

        // Check sub-fleet exclusion / inclusion
        const isHiddenForSubFleet = hiddenForSubFleet && isSubFleet;
        const missesRequiredSubFleet = requiresSubFleet && !isSubFleet;

        // Environment-scoped tooling pages stay out of the search results in
        // production too (review finding F-29, R-33).
        const isHiddenInProduction =
          hiddenInProduction && getPortalDataEnvironment() === 'production';

        // Partner-facing duplicates of internal-folder pages (e.g. Platform Status & Alerts).
        const isHiddenForInternal = hiddenForInternalUsers && isInternalUser;

        return (
          hasRoles &&
          hasGroup &&
          hasPartnerType &&
          !isExcluded &&
          !isHiddenForSubFleet &&
          !missesRequiredSubFleet &&
          !isHiddenInProduction &&
          !isHiddenForInternal
        );
      };

      const hasRequiredRolesAndGroup = (
        itemRoles,
        itemGroup,
        itemPartnerTypes,
        itemExcludedPartners,
        itemHiddenForSubFleet,
        itemRequiresSubFleet,
        itemHiddenInProduction,
        itemHiddenForInternalUsers,
      ) => {
        return checkAccess({
          roles: itemRoles,
          groups: itemGroup,
          partnerTypes: itemPartnerTypes,
          excludedPartners: itemExcludedPartners,
          hiddenForSubFleet: itemHiddenForSubFleet,
          requiresSubFleet: itemRequiresSubFleet,
          hiddenInProduction: itemHiddenInProduction,
          hiddenForInternalUsers: itemHiddenForInternalUsers,
        });
      };

      const searchItems = (menuItems, parentTitles = []) => {
        menuItems.forEach((menuItem) => {
          if (
            menuItem.title &&
            hasRequiredRolesAndGroup(
              menuItem.requiredRoles,
              menuItem.requiredUserGroup,
              menuItem.requiredPartnerTypes,
              menuItem.excludedPartners,
              menuItem.hiddenForSubFleet,
              menuItem.requiresSubFleet,
              menuItem.hiddenInProduction,
              menuItem.hiddenForInternalUsers,
            )
          ) {
            const displayPath = [...parentTitles, menuItem.title].join(' -> ');
            if (displayPath.toLocaleLowerCase().includes(searchval) && menuItem.type === 'link') {
              items.push({ ...menuItem, displayPath, type: 'menu' });
            }
            if (menuItem.children) {
              searchItems(menuItem.children, [...parentTitles, menuItem.title]);
            }
          }
        });
      };

      searchItems(this.menuItems);

      const filteredPageContents = this.pageContents.filter((content) => {
        if (content.requirements) {
          return content.requirements.every((req) => checkAccess(req));
        }
        return hasRequiredRolesAndGroup(
          content.requiredRoles,
          content.requiredUserGroup,
          content.requiredPartnerTypes,
          content.excludedPartners,
          content.hiddenForSubFleet,
          content.requiresSubFleet,
          content.hiddenInProduction,
          content.hiddenForInternalUsers,
        );
      });

      const foundPaths = new Set(items.map((item) => item.path));

      const pageResults = filteredPageContents
        .filter(
          (content) =>
            !foundPaths.has(content.path) && content.content.toLowerCase().includes(searchval),
        )
        .map((content) => ({
          ...content,
          contentSnippet: this.extractContext(content.content, searchval),
        }));

      const faqResults = filteredPageContents.flatMap((content) =>
        (content.faqs || [])
          .filter(
            (faq) =>
              faq.q.toLowerCase().includes(searchval) || faq.a.toLowerCase().includes(searchval),
          )
          .map((faq) => ({
            ...content,
            type: 'faq',
            faqQuestion: faq.q,
            faqAnswer: faq.a,
          })),
      );

      this.results = [...items, ...faqResults, ...pageResults];
      this.$store.dispatch('menu/setSearchData', items);
      this.searchResultEmpty = this.results.length === 0;
      this.isLoading = false;
    },
    extractContext(content, searchTerm) {
      const startIdx = content.toLowerCase().indexOf(searchTerm.toLowerCase());
      if (startIdx === -1) return content.substring(0, 100);
      const endIdx = startIdx + searchTerm.length;
      return (
        '...' +
        content.substring(Math.max(0, startIdx - 20), Math.min(content.length, endIdx + 20)) +
        '...'
      );
    },
    addFix() {
      document.body.classList.add('offcanvas');
      this.searchResult = true;
    },
    setActiveLink(item) {
      this.results = []; // Clear results but keep search open
      this.$store.dispatch('menu/setNavActive', item);
      this.$router.replace({ path: item.path });
      this.search_close();
    },
    removeFix() {
      document.body.classList.remove('offcanvas');
      document.body.classList.remove('no-scroll');
      this.searchResult = false;
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/utils/_variables.scss';

.found-item {
  cursor: pointer;
}

.faq-answer {
  margin-bottom: 0.25rem;
}

#foundResultMenu.Typeahead-menu {
  position: relative;
}

.Typeahead-menu-container {
  max-height: 85vh;
  overflow-y: auto;
}

.highlight {
  font-weight: bold;
  color: var(--theme-default);
}

.ProfileCard.found-item.is-highlighted {
  background-color: rgba($primary-color, 0.08);
  outline: 1px solid rgba($primary-color, 0.25);
}

.no-scroll {
  overflow: hidden;
}
</style>
